import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import marketplacesettingsService from '../services/marketplacesettingsService';
import legacyUapiService from '../services/LegacyUapiService';
import hotelService from '../services/hotelService';
import useCredentialStep from './useCredentialStep';
import WizardStepper from './WizardStepper';
import WizardHotelStep from './WizardHotelStep';
import WizardPmsStep from './WizardPmsStep';
import WizardCredentialsStep from './WizardCredentialsStep';
import CopyButton from './shared/CopyButton';
import Spinner from './shared/Spinner';
import './AddPaymentPorterWizard.css';
import './UniversalApiPage.css';
import WizardReview from './WizardReview';

// Legacy > Universal API > Authentication and Access — provision a property end to end.
//
// The screen this starts from lists the INBOUND half: the credential a caller presents to
// uapi.hconnect.io. That is only half an integration. A call that authenticates still has to be routed
// somewhere, with something to authenticate to that system with — and until V419 nothing recorded which
// system, or which credential, for a given property. This wizard produces both in one pass:
//
//   Hotel → backend system → the credential the uAPI calls that system with → save
//
// SAVE DOES TWO WRITES, in this order:
//   1. The hotel's inbound Universal API credential, if it does not have one yet: minted (a real Cognito
//      app client — nothing else authenticates against the gateway) and stored through the ordinary
//      credential save, so there is exactly one write path for a credential on this platform.
//   2. The binding row (uapi_hotel_access), which the lambda stamps with the x-hotelid the caller will
//      present.
//
// ONE INBOUND CREDENTIAL PER HOTEL. A property reachable through two systems does not get two
// credentials — it gets two bindings against the one credential, each with its own x-hotelid. So the
// mint step is skipped, deliberately and visibly, when the hotel already holds one.
//
// The issued secret is shown ONCE, on the final step, and is not retrievable afterwards: it exists in
// the generate response and nowhere readable. That is why the wizard ends on a result screen rather than
// navigating straight back to the list.

// ACCESS, BEFORE REVIEW. A Universal API credential is issued able to call nothing but /oauth, so a binding
// made here authenticated and was then refused on every call ("/rsv is not granted to these credentials")
// until somebody found the credential's own page. What it may call is decided here, while it is being set up:
// an access profile, or modules granted directly (V522 grant modes).
const STEPS = [
    { num: 1, label: 'Hotel' },
    { num: 2, label: 'Backend system' },
    { num: 3, label: 'Credential' },
    { num: 4, label: 'Access' },
    { num: 5, label: 'Review' },
];

const LIST_URL = '/universal-api/authentication';
const hotelUrl = (hotelId) => `${LIST_URL}/${hotelId}`;

// The values the generate endpoint mints, by the marketplace setting they belong in. Only names the
// marketplace actually defines are written (V413 seeded four, V414 copied OHIP's AUTH set on top, V415
// removed one) — writing a setting the marketplace does not define stores a value no form ever renders.
const GENERATED_SETTINGS = (issued) => ({
    CLIENTID: issued.clientId,
    CLIENTSECRET: issued.clientSecret,
    TOKENURL: issued.tokenUrl,
    SCOPE: issued.scope,
    APPKEY: issued.appKey,
    ENTERPRISEID: issued.enterpriseId,
    EXTERNALSYSTEM: issued.externalSystem,
});

const errText = (e, fallback) =>
    e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fallback;

// EMBEDDED (props), as well as a page. The pipeline implementation wizard opens this inline on its Target
// Credentials step, for a property that has no Universal API credential or no binding to the system a
// route needs — one flow for issuing a credential and binding a backend, not a second copy of it.
//   hotelId        the property, settled — the hotel step is skipped, as when opened from a hotel's page
//   marketplaceId  optional: only this backend system is offered, and it is chosen already
//   onDone(access) called from the result screen in place of navigating away
//   onCancel()     called where the page would navigate back
//   suggestedModules  optional: module paths to tick on the Access step (the route's own, from the pipeline)
const UapiAccessWizard = ({ hotelId: hotelIdProp, marketplaceId: onlyMarketplaceId, onDone, onCancel,
    suggestedModules } = {}) => {
    const routerNavigate = useNavigate();
    const embedded = !!(onDone || onCancel);
    // Reached two ways. From the list with no hotel in hand (four steps), or from ONE hotel's page to
    // add another backend system to it (three steps — the property is already settled, and asking again
    // invites picking a different one by accident).
    const { hotelId: hotelIdRouteParam } = useParams();
    const hotelIdParam = hotelIdProp || hotelIdRouteParam;
    // Leaving: back to the host when embedded, to the page's own return url otherwise.
    const navigate = (url) => (embedded ? (onCancel && onCancel()) : routerNavigate(url));
    const { accounts, selectedAccount, selectedPartner, isChainAdmin } = useAccount();
    const { isSuperAdmin, isPartner, solePartnerId } = useAuth();

    const [step, setStep] = useState(1);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Step 1 — THE CHAIN COMES FROM THE NAV BAR, and cannot be chosen here.
    //
    // This screen used to render its own chain dropdown, on the reasoning that it is reached with no
    // hotel context. That put two chain selectors on screen at once saying different things: the nav bar
    // is the context every other screen obeys, and a wizard that quietly works against a different chain
    // is how a property ends up provisioned under the wrong one. Switching chain is a nav bar action, in
    // this flow as in every other.
    const [selectedHotel, setSelectedHotel] = useState(null);

    // ENTERED FROM ONE HOTEL, the chain is that HOTEL'S, not the nav bar's. The property is already
    // settled in that flow, and its chain is a fact about it — reading the bar instead would list the
    // wrong chain's credentials whenever the two disagree, which they can, because a hotel page is
    // reachable by link.
    const [hotelAccountId, setHotelAccountId] = useState(null);
    const accountId = hotelIdParam ? hotelAccountId : (selectedAccount || null);

    // A chain change in the nav bar invalidates a hotel already picked from the previous one — it is not
    // in the new chain's list, and carrying it forward would provision it under a chain the reader is no
    // longer looking at.
    useEffect(() => {
        if (hotelIdParam) return;      // entered from one hotel; the property is settled
        setSelectedHotel(null);
        setStep(1);
    }, [selectedAccount]); // eslint-disable-line

    // A name for THIS binding, so a hotel's second binding to the same system is identifiable (V454).
    // Optional: a hotel's only binding for a system is identified well enough by the system name, and
    // requiring a label would make everyone type one to no purpose.
    const [label, setLabel] = useState('');

    // Step 2 — systems switched on for the Universal API (marketplace.uapi_enabled, V417), resolved by
    // the server so a stale client-side list cannot offer one that is switched off.
    const [systems, setSystems] = useState([]);
    const [boundSystemIds, setBoundSystemIds] = useState([]);   // already bound for this hotel
    const [selectedMarketplace, setSelectedMarketplace] = useState(null);

    // The hotel's existing inbound credential, if any — decided at step 1 so the review step can say
    // whether one will be issued rather than surprising the reader on save.
    const [inboundCredential, setInboundCredential] = useState(null);
    const [uapiMarketplaceId, setUapiMarketplaceId] = useState(null);

    const [result, setResult] = useState(null);   // { access, issued } — the final screen

    // ── Step 4: what the credential may call ────────────────────────────────
    // DIRECT: modules granted to this credential. PROFILE: an access profile governs it (one profile, one
    // redaction — V465/V522). An existing credential opens on what it has now; a new one on DIRECT, with the
    // modules the host suggested (a pipeline route's own) already ticked.
    const [accessMode, setAccessMode] = useState('DIRECT');
    const [rootRoutes, setRootRoutes] = useState([]);           // [{ id, path }]
    const [accessProfiles, setAccessProfiles] = useState([]);
    const [profileId, setProfileId] = useState('');
    const [pickedModules, setPickedModules] = useState(new Set());   // uapi_routes ids
    const [grantedPaths, setGrantedPaths] = useState([]);       // already on an existing credential
    const [accessLoaded, setAccessLoaded] = useState(false);
    const [accessWarning, setAccessWarning] = useState(null);

    // ── A SYSTEM WITH NO BACKEND TO SIGN IN TO (V516) ───────────────────────
    //
    // Most bindings name a system the Universal API CALLS, and the credential step asks how to sign in
    // to it. A hub_served system is not called: the message is queued here and the vendor collects it
    // with the Universal API credential it already holds (GET /mq). There is nothing of theirs to
    // authenticate to.
    //
    // So the credential step does not apply, and asking anyway was not a small annoyance — it was an
    // empty picker, a disabled Next and no way forward. A question that cannot be answered must not be
    // asked; it has to be skipped, and the review step has to say why.
    const queueServed = !!selectedMarketplace
        && (selectedMarketplace.hub_served === 'Y'
            || selectedMarketplace.hub_to_marketplace === 'MQ_POLL');

    const cs = useCredentialStep({
        selectedMarketplace,
        selectedHotel,
        selectedAccount: accountId,
        selectedPartner,
        solePartnerId,
        isSuperAdmin,
        isPartner,
        isChainAdmin,
    });

    const partnerId = selectedPartner || solePartnerId;

    // ── Step 1 → 2 ──────────────────────────────────────────────────────────
    // Everything the later steps need about the hotel is loaded here, together: which systems it can be
    // routed to, which it is already bound to, and whether it already holds an inbound credential.
    const loadForHotel = async (hotel) => {
        setErrors({});
        setLoading(true);
        try {
            const [picker, accessRes, credsRes] = await Promise.all([
                entityMktSettingsService.getPicker({
                    uapiEnabled: 'Y',
                    hotelId: hotel.hotelid,
                    accountId,
                    partnerId,
                }),
                legacyUapiService.listAccess(hotel.hotelid),
                legacyUapiService.listCredentials({ hotelId: hotel.hotelid }),
            ]);
            const offered = picker.marketplaces || [];
            setSystems(onlyMarketplaceId
                ? offered.filter(m => String(m.id) === String(onlyMarketplaceId))
                : offered);

            const access = (accessRes.data || accessRes).access || [];
            setBoundSystemIds(access.map(a => String(a.marketplace_id)));

            const credData = credsRes.data || credsRes;
            setUapiMarketplaceId(credData.marketplaceId || null);
            setInboundCredential((credData.credentials || [])[0] || null);

            // One system asked for by the host: it is the answer, so it is chosen already.
            const only = onlyMarketplaceId
                ? offered.find(m => String(m.id) === String(onlyMarketplaceId)) || null
                : null;
            setSelectedMarketplace(only);
            setStep(2);
        } catch (e) {
            setErrors({ hotel: errText(e, 'Could not load the systems for this hotel') });
        } finally {
            setLoading(false);
        }
    };

    const goToSystem = () => {
        if (!selectedHotel) { setErrors({ hotel: 'Please select a hotel' }); return; }
        return loadForHotel(selectedHotel);
    };

    // Entered from a hotel's own page: resolve the property, then run the identical load and open on
    // the system step.
    useEffect(() => {
        if (!hotelIdParam) return;
        let cancelled = false;
        setLoading(true);
        hotelService.getHotelById(hotelIdParam)
            .then((res) => {
                const h = res?.hotel || res?.hotels?.[0] || res;
                if (cancelled || !h?.hotelid) return undefined;
                setSelectedHotel(h);
                if (h.accountid) setHotelAccountId(h.accountid);
                return loadForHotel(h);
            })
            .catch((e) => { if (!cancelled) setErrors({ hotel: errText(e, 'Could not load that hotel') }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [hotelIdParam]); // eslint-disable-line

    // ── Step 2 → 3 ──────────────────────────────────────────────────────────
    const goToCredential = async () => {
        if (!selectedMarketplace) { setErrors({ marketplace: 'Please select a backend system' }); return; }
        setErrors({});

        // NOTHING TO ASK. Straight to review, and nothing is loaded for a picker that will not be
        // shown — the credential list for a queue-served system is empty by definition.
        if (queueServed) { goToAccess(); return; }

        setLoading(true);
        try {
            // The credentials the picker offers come from the shared step; this only primes the hook's
            // own list so its validation ("pick one of the credentials that exist") has something to
            // check against — exactly what the Payment Porter wizard does at the same point.
            const fetchers = [entityMktSettingsService.listCredentials('hotel', selectedHotel.hotelid)];
            const levels = ['hotel'];
            if (accountId) { fetchers.push(entityMktSettingsService.listCredentials('account', accountId)); levels.push('account'); }
            if (partnerId) { fetchers.push(entityMktSettingsService.listCredentials('partner', partnerId)); levels.push('partner'); }
            const results = await Promise.all(fetchers);
            cs.setCredentials(results.flatMap((r, i) =>
                (r.credentials || []).map(c => ({ ...c, _entityType: levels[i] }))
            ));
            await cs.loadForMarketplace();
            setStep(3);
        } catch (e) {
            setErrors({ marketplace: errText(e, 'Could not load credentials for that system') });
        } finally {
            setLoading(false);
        }
    };

    // ── → Step 4 (Access) ───────────────────────────────────────────────────
    // Loaded once: the modules the Universal API publishes (its root routes, less /oauth which every
    // credential holds), the access profiles, and — for a credential the hotel already has — what it is
    // allowed now, so the step adds to that rather than pretending it starts empty.
    const loadAccess = async () => {
        if (accessLoaded) return;
        try {
            const [routesRes, profilesRes, currentRes] = await Promise.all([
                legacyUapiService.listRoutes(),
                legacyUapiService.listAccessProfiles(),
                inboundCredential ? legacyUapiService.listCredentialModules(inboundCredential.credential_id) : null,
            ]);
            const roots = (((routesRes.data || routesRes).routes) || [])
                .filter(r => r.parent_id == null && r.path !== '/oauth')
                .map(r => ({ id: r.id, path: r.path }))
                .sort((a, b) => String(a.path).localeCompare(String(b.path)));
            setRootRoutes(roots);
            setAccessProfiles(((profilesRes.data || profilesRes).profiles) || []);

            const current = currentRes ? (currentRes.data || currentRes) : null;
            const granted = ((current && current.modules) || []).map(m => m.path || m.uapi_route_path).filter(Boolean);
            setGrantedPaths(granted);
            if (current && current.grantMode === 'PROFILE') {
                setAccessMode('PROFILE');
                if (current.profile) setProfileId(String(current.profile.id));
            }
            const want = new Set((suggestedModules || []).map(String));
            setPickedModules(new Set(roots.filter(r => want.has(r.path) && !granted.includes(r.path)).map(r => r.id)));
            setAccessLoaded(true);
        } catch (e) {
            setErrors({ access: errText(e, 'Could not load the modules and access profiles') });
        }
    };
    const goToAccess = () => { setErrors({}); setStep(4); loadAccess(); };

    // ── Step 3 → 4 ──────────────────────────────────────────────────────────
    const goToReview = () => {
        const err = cs.validate();
        if (err) { setErrors(err); return; }
        goToAccess();
    };

    const togglePicked = (id) => setPickedModules(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const pickedPaths = rootRoutes.filter(r => pickedModules.has(r.id)).map(r => r.path);
    const chosenProfile = accessProfiles.find(p => String(p.id) === String(profileId)) || null;
    const accessSummary = accessMode === 'PROFILE'
        ? (chosenProfile ? `Access profile: ${chosenProfile.name}` : 'Access profile — none chosen')
        : [...grantedPaths.filter(p => p !== '/oauth'), ...pickedPaths].join(', ') || 'Nothing beyond /oauth — every call will be refused';

    // ── Save ────────────────────────────────────────────────────────────────
    const save = async () => {
        setSaving(true);
        setErrors({});
        let issued = null;
        let inboundId = inboundCredential?.credential_id || null;
        try {
            // 1. Issue the inbound credential, only if the hotel has none. Minted first: if the binding
            //    insert then fails the property is left with a credential and no route, which is
            //    recoverable by running the wizard again — the reverse would leave a binding pointing at
            //    a credential that was never stored.
            if (!inboundId) {
                if (!uapiMarketplaceId) {
                    throw new Error('the HCONNECT Universal API marketplace is not present in this environment');
                }
                const credentialName = `${selectedHotel.hotelname} Universal API`;
                const genRes = await entityMktSettingsService.generateUapiCredentials({ credentialName });
                issued = genRes.data || genRes;

                const schemaRes = await marketplacesettingsService.getSchema({ MARKETPLACEID: uapiMarketplaceId });
                const values = GENERATED_SETTINGS(issued);
                const settings = (schemaRes.settings || [])
                    .filter(s => values[s.SETTINGNAME] !== undefined && values[s.SETTINGNAME] !== null)
                    .map(s => ({ name: s.SETTINGNAME, value: values[s.SETTINGNAME] }));

                const saved = await entityMktSettingsService.saveCredentials(
                    'hotel', selectedHotel.hotelid, uapiMarketplaceId, credentialName, settings,
                );
                inboundId = saved?.credentialId || null;
                // REMEMBERED AT ONCE, not after the binding. If step 2 fails, pressing Create again must
                // reuse this credential — it used to mint a second one, and the server (which holds a
                // hotel to its one credential) then refused the binding for naming the newer of the two.
                if (inboundId) setInboundCredential({ credential_id: inboundId, credential_name: credentialName });
            }

            // 2. The binding. The x-hotelid comes back from the server, which mints it.
            //
            // NO PAYLOAD FOR A QUEUE-SERVED SYSTEM. The credential step never ran, so the hook holds
            // nothing — calling buildCredentialPayload() would read an unanswered form and send
            // whatever its empty state happens to be. The binding is created with no backend
            // credential, which is the truth: there is no backend to sign in to.
            const payload = queueServed ? {} : cs.buildCredentialPayload();
            const res = await legacyUapiService.createAccess({
                hotelId: selectedHotel.hotelid,
                marketplaceId: selectedMarketplace.id,
                credentialId: payload.credential_id || null,
                hotelidOverride: (queueServed ? null : cs.hotelIdOverride) || null,
                credentialOverrides: payload.credential_overrides || null,
                uapiCredentialId: inboundId,
                label: label.trim() || null,
            });
            const access = res.data || res;
            // 3. What the credential may call. After the binding: the credential exists by now whether it was
            //    issued in this run or not. A failure here does NOT undo the binding — it is real and works
            //    for authentication — and the message says where to finish it.
            let warning = null;
            if (inboundId) {
                try {
                    if (accessMode === 'PROFILE') {
                        if (profileId) {
                            await legacyUapiService.setCredentialGrantMode(inboundId, 'PROFILE');
                            await legacyUapiService.setCredentialAccessProfile(inboundId, Number(profileId));
                        }
                    } else {
                        await legacyUapiService.setCredentialGrantMode(inboundId, 'DIRECT');
                        if (pickedModules.size) {
                            await legacyUapiService.addCredentialModules(inboundId, [...pickedModules].map(Number));
                        }
                    }
                } catch (e) {
                    warning = `The access was created, but its modules could not be set (${errText(e, 'unknown error')}). `
                        + 'Set them on the hotel\'s Universal API page before sending anything.';
                }
            }
            setAccessWarning(warning);
            setResult({ access, issued });
            setStep(6);
        } catch (e) {
            // An inbound credential issued before the failure is NOT rolled back, and the message says
            // so: it is a real credential on a real hotel, and silently discarding it here would leave
            // an app client nothing references.
            setErrors({
                save: errText(e, 'Could not save the access')
                    + (issued ? ' — the Universal API credential was issued and saved; re-run the wizard for this hotel to finish the binding.' : ''),
            });
        } finally {
            setSaving(false);
        }
    };

    // EVERY system is offered, including ones this hotel is already bound to. A property can be
    // reachable through two OHIP tenancies, through one tenancy under two property codes, or through two
    // credentials with deliberately different module grants — and a binding already carries its own
    // credential, hotelid_override, credential overrides and module list, so all three are expressible.
    // V454 dropped the unique key that used to forbid it; the x-hotelid stays unique, which is the only
    // uniqueness anything actually resolves on.
    //
    // Already-bound systems are still MARKED, because adding a second binding by accident is a real
    // mistake and the count is the warning.
    const boundCount = (id) => boundSystemIds.filter(b => b === String(id)).length;
    const availableSystems = systems;
    // The nav bar's chain, by name. `accounts` is the same list the bar is populated from, so the wizard
    // shows the reader the words they selected up there rather than an id.
    const chainName = accounts?.find(a => String(a.id) === String(accountId))?.name || null;
    const credentialLevel = cs.selectedCredential?._entityType;

    // With the hotel settled the first step is not shown, so it is not numbered either — a stepper that
    // opens on "2" describes a step the reader never had.
    // THE INDICATOR SHOWS THE STEPS THIS RUN ACTUALLY HAS. A 'Credential' pip that is skipped is worse
    // than no pip: the reader watches the wizard jump over it and wonders what they missed.
    const visibleSteps = (hotelIdParam ? STEPS.slice(1) : STEPS)
        .filter(s => !(queueServed && s.label === 'Credential'))
        .map((s, i) => ({ ...s, num: i + 1 }));
    // …and the CURRENT pip has to be counted the same way, or Review lights up the pip before it.
    // `step` is the wizard's own numbering (4 = Review, always); this is its position among the steps
    // that are actually being shown.
    const visibleStep = (hotelIdParam ? step - 1 : step)
        - (queueServed && step >= 4 ? 1 : 0);
    // Where Back and Done go: the hotel's own page when the wizard was opened from it, else the list.
    const returnUrl = hotelIdParam ? hotelUrl(hotelIdParam) : LIST_URL;

    return (
        <div className={embedded ? 'uapi-embedded' : 'ppw-page'}>
            {/* Embedded, the host's stepper is the one on screen; a second would read as a second wizard. */}
            {step <= 5 && !embedded && <WizardStepper steps={visibleSteps} currentStep={visibleStep} />}

            {step === 1 && !hotelIdParam && (
                <WizardHotelStep
                    title="Select hotel"
                    subtitle={chainName
                        ? `The property being given access to the Universal API, in ${chainName}. `
                          + 'Switch chain in the bar above.'
                        : 'The property being given access to the Universal API. Choose a hotel chain in '
                          + 'the bar above to narrow the list.'}
                    accountId={accountId}
                    partnerId={partnerId}
                    selectedHotel={selectedHotel}
                    onSelect={(h) => { setSelectedHotel(h); setErrors({}); }}
                    error={errors.hotel}
                    loading={loading}
                    onBack={() => navigate(LIST_URL)}
                    onNext={goToSystem}
                />
            )}

            {step === 2 && (
                <WizardPmsStep
                    title="Select backend system"
                    subtitle={
                        boundSystemIds.length
                            ? `The system the Universal API calls for ${selectedHotel?.hotelname}. `
                              + `This hotel already has ${boundSystemIds.length} binding(s); a system can be `
                              + 'bound more than once — each binding gets its own x-hotelid, credential and '
                              + 'property code. Give a second one a name so they can be told apart.'
                            : `The system the Universal API calls for ${selectedHotel?.hotelname}. `
                              + 'Only systems switched on for the Universal API appear here — turn one on in '
                              + 'Admin › Marketplace, on the system itself.'
                    }
                    marketplaces={availableSystems}
                    selectedMarketplace={selectedMarketplace}
                    onSelect={(m) => { setSelectedMarketplace(m); setErrors({}); }}
                    error={errors.marketplace}
                    onBack={() => (hotelIdParam ? navigate(returnUrl) : setStep(1))}
                    onNext={goToCredential}
                />
            )}

            {step === 3 && (
                <WizardCredentialsStep
                    selectedMarketplace={selectedMarketplace}
                    selectedHotel={selectedHotel}
                    accountId={accountId}
                    partnerId={partnerId}
                    cs={cs}
                    errors={errors}
                    onClearError={(key) => setErrors(prev => ({ ...prev, [key]: undefined }))}
                    onBack={() => setStep(2)}
                    onNext={goToReview}
                />
            )}

            {step === 4 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Access</h2>
                        <p className="ppw-card-subtitle">
                            What {inboundCredential ? inboundCredential.credential_name : `${selectedHotel?.hotelname} Universal API`}
                            {' '}may call. A Universal API credential starts with /oauth only — without this, every
                            call it makes is refused.
                        </p>
                    </div>
                    <div className="ppw-card-body">
                        <div className="uapi-access-mode" role="radiogroup" aria-label="Access comes from">
                            <label className="uapi-access-opt">
                                <input type="radio" name="uapi-access-mode" checked={accessMode === 'DIRECT'}
                                    onChange={() => setAccessMode('DIRECT')} />
                                <span><strong>Modules</strong> — granted to this credential</span>
                            </label>
                            <label className="uapi-access-opt">
                                <input type="radio" name="uapi-access-mode" checked={accessMode === 'PROFILE'}
                                    onChange={() => setAccessMode('PROFILE')} />
                                <span><strong>Access profile</strong> — a shared set of modules and redactions</span>
                            </label>
                        </div>

                        {!accessLoaded && !errors.access && <div className="loading-section"><Spinner size="md" /></div>}

                        {accessLoaded && accessMode === 'DIRECT' && (
                            <div className="ppw-field">
                                <label className="ppw-label">
                                    Modules <span className="ppw-label-hint">
                                        {suggestedModules && suggestedModules.length ? '(the route\'s own are ticked)' : ''}
                                    </span>
                                </label>
                                <div className="uapi-access-modules">
                                    {rootRoutes.map(r => {
                                        const already = grantedPaths.includes(r.path);
                                        return (
                                            <label key={r.id} className={`uapi-access-mod${already ? ' is-granted' : ''}`}>
                                                <input type="checkbox" disabled={already}
                                                    checked={already || pickedModules.has(r.id)}
                                                    onChange={() => togglePicked(r.id)} />
                                                <code>{r.path}</code>
                                                {already && <span className="ppw-label-hint">granted</span>}
                                            </label>
                                        );
                                    })}
                                </div>
                                <div className="uapi-access-actions">
                                    <button type="button" className="ppw-btn-back"
                                        onClick={() => setPickedModules(new Set(rootRoutes.filter(r => !grantedPaths.includes(r.path)).map(r => r.id)))}>
                                        Select all
                                    </button>
                                    <button type="button" className="ppw-btn-back" onClick={() => setPickedModules(new Set())}>
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}

                        {accessLoaded && accessMode === 'PROFILE' && (
                            <div className="ppw-field">
                                <label className="ppw-label" htmlFor="uapi-access-profile">Access profile</label>
                                <select id="uapi-access-profile" className="ppw-select" value={profileId}
                                    onChange={(e) => setProfileId(e.target.value)}>
                                    <option value="">Choose a profile…</option>
                                    {accessProfiles.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}{p.module_count != null ? ` — ${p.module_count} module(s)` : ''}
                                        </option>
                                    ))}
                                </select>
                                <span className="ppw-label-hint">
                                    Exactly one profile governs a credential; choosing one here makes it this
                                    credential's, in place of any it inherits from its hotel or chain.
                                </span>
                            </div>
                        )}

                        {errors.access && <span className="ppw-error-msg">{errors.access}</span>}
                    </div>
                    <div className="ppw-footer">
                        <button className="ppw-btn-back" onClick={() => setStep(queueServed ? 2 : 3)}>← Back</button>
                        <button className="ppw-btn-next" onClick={() => { setErrors({}); setStep(5); }}
                            disabled={!accessLoaded || (accessMode === 'PROFILE' && !profileId)}>
                            Next →
                        </button>
                    </div>
                </div>
            )}

            {step === 5 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Review</h2>
                        <p className="ppw-card-subtitle">
                            What will be created. The routing identifier is issued on save.
                        </p>
                    </div>
                    <div className="ppw-card-body">
                        <WizardReview rows={[
                                ['Hotel', selectedHotel?.hotelname],
                                ['Chain', chainName || '—'],
                                ['Backend system', selectedMarketplace?.name],
                                // No "Name for this binding" row: the field below is where it is set, and a
                                // row echoing it as it is typed reads as being asked twice.
                                // WHY THERE IS NO CREDENTIAL, said here rather than left as a blank
                                // row. A reader who has just been taken past a step is owed the reason,
                                // and "—" beside "Credential" reads as something that failed to load.
                                ...(queueServed ? [
                                    ['How it is delivered',
                                        `${selectedMarketplace?.name} is not called — messages are queued `
                                        + 'on the Universal API and it collects them from GET /mq'],
                                    ['Credential',
                                        'None needed — it polls with the Universal API credential it already holds'],
                                ] : [
                                    ['Credential', cs.selectedCredential?.credential_name],
                                    ['Credential level', credentialLevel ? credentialLevel.replace(/^./, c => c.toUpperCase()) : '—'],
                                    ['Property code for this system', cs.hotelIdOverride || '— (on the credential itself)'],
                                ]),
                                ['What it may call', accessSummary],
                                ['Universal API credential', inboundCredential
                                    ? `${inboundCredential.credential_name} (already issued — reused)`
                                    : 'Will be issued and shown once on the next screen'],
                            ]} />
                        {/* Asked for on the REVIEW step, not earlier: it is only worth typing once the
                            system is chosen, and this is where the reader can see it is the second
                            binding to that system and that the two need telling apart. */}
                        <div className="ppw-field">
                            <label className="ppw-label" htmlFor="uapi-binding-label">
                                Name for this binding <span className="ppw-label-hint">(optional)</span>
                            </label>
                            <input
                                id="uapi-binding-label"
                                className="ppw-input"
                                type="text"
                                maxLength={255}
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                placeholder={boundCount(selectedMarketplace?.id) > 0
                                    ? `e.g. "${selectedMarketplace?.name} — second tenancy"`
                                    : 'Left blank, the system name is used'}
                            />
                            {boundCount(selectedMarketplace?.id) > 0 && (
                                <span className="ppw-label-hint">
                                    This hotel already has {boundCount(selectedMarketplace?.id)} binding(s) to
                                    {' '}{selectedMarketplace?.name}. A name is what will tell them apart in the list.
                                </span>
                            )}
                        </div>
                        {errors.save && <span className="ppw-error-msg">{errors.save}</span>}
                    </div>
                    <div className="ppw-footer">
                        {/* Back to Access, the step before this one for every system. (Access's own Back
                            skips Credential for a queue-served system, which never had one.) */}
                        <button className="ppw-btn-back" onClick={() => setStep(4)}
                            disabled={saving}>← Back</button>
                        <button className="ppw-btn-next" onClick={save} disabled={saving}>
                            {saving ? 'Saving…' : 'Create access'}
                        </button>
                    </div>
                </div>
            )}

            {step === 6 && result && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Access created</h2>
                        <p className="ppw-card-subtitle">
                            {selectedHotel?.hotelname} is now reachable through {selectedMarketplace?.name}.
                        </p>
                    </div>
                    <div className="ppw-card-body">
                        {accessWarning && <div className="ppw-error-msg">{accessWarning}</div>}
                        {/* The routing identifier. A caller presents it as x-hotelid on every request, so
                            it is the one value that always has to be handed over — issued or not. */}
                        <div className="uapi-issued">
                            <div className="uapi-issued-title">Routing identifier (x-hotelid)</div>
                            <div className="uapi-issued-row">
                                <span className="uapi-issued-label">x-hotelid</span>
                                <code className="uapi-issued-value">{result.access.uapi_hotel_id}</code>
                                <CopyButton value={result.access.uapi_hotel_id} label="Copy" className="copy-btn-inline" />
                            </div>
                        </div>

                        {/* Shown ONCE. The secret is not retrievable afterwards — it exists in the
                            generate response and nowhere readable — so a reader who leaves without
                            copying it has to regenerate, which breaks the caller a second time. */}
                        {result.issued && (
                            <div className="uapi-issued">
                                <div className="uapi-issued-title">
                                    Universal API credential issued — copy the secret now, it is not shown again
                                </div>
                                {[['Client ID', result.issued.clientId], ['Client Secret', result.issued.clientSecret],
                                  ['Token URL', result.issued.tokenUrl], ['App Key', result.issued.appKey],
                                  ['Enterprise ID', result.issued.enterpriseId],
                                  ['External System', result.issued.externalSystem]].filter(([, v]) => v).map(([label, value]) => (
                                    <div key={label} className="uapi-issued-row">
                                        <span className="uapi-issued-label">{label}</span>
                                        <code className="uapi-issued-value">{value}</code>
                                        <CopyButton value={value} label="Copy" className="copy-btn-inline" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="ppw-footer">
                        {embedded ? (
                            <button className="ppw-btn-next" onClick={() => onDone && onDone(result.access, result.issued)}>
                                Use this access
                            </button>
                        ) : (<>
                        <button className="ppw-btn-back" onClick={() => navigate(returnUrl)}>Done</button>
                        <button
                            className="ppw-btn-next"
                            onClick={() => {
                                // Another system for the SAME hotel: keep the hotel and its inbound
                                // credential, drop the system-specific answers.
                                setBoundSystemIds(prev => [...prev, String(selectedMarketplace.id)]);
                                setSelectedMarketplace(null);
                                cs.setSelectedCredential(null);
                                setResult(null);
                                // The credential now exists, so the next pass reads what it holds.
                                setAccessLoaded(false);
                                setPickedModules(new Set());
                                setStep(2);
                            }}
                        >
                            Add another system
                        </button>
                        </>)}
                    </div>
                </div>
            )}

            {loading && step <= 3 && (
                <div className="loading-section"><Spinner size="md" /><p>Loading…</p></div>
            )}
        </div>
    );
};

export default UapiAccessWizard;
